
import React from "react";
import { Check, X, CalendarClock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { StatusBadge } from "./StatusBadge";
import { MentorApplication } from "./MentorTypes";

interface MentorDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mentor: MentorApplication | null;
  onUpdateStatus: (id: string, status: 'approved' | 'rejected') => Promise<void>;
}

const MentorDetailsDialog: React.FC<MentorDetailsDialogProps> = ({
  open,
  onOpenChange,
  mentor,
  onUpdateStatus
}) => {
  if (!mentor) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Mentor Details</DialogTitle>
          <DialogDescription>
            Application from {mentor.profile?.full_name}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="flex items-center gap-4">
            <Avatar className="h-12 w-12">
              <AvatarImage src={mentor.profile?.avatar_url} />
              <AvatarFallback>
                {mentor.profile?.full_name?.charAt(0) || "?"}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="font-medium">{mentor.profile?.full_name}</h3>
              <p className="text-sm text-muted-foreground">
                {mentor.position} at {mentor.company}
              </p>
            </div>
          </div>
          
          <div>
            <h4 className="text-sm font-medium mb-1">Bio</h4>
            <p className="text-sm">{mentor.bio || "No bio provided"}</p>
          </div>
          
          <div>
            <h4 className="text-sm font-medium mb-1">Expertise</h4>
            <div className="flex flex-wrap gap-1">
              {mentor.expertise?.map((skill, i) => (
                <Badge key={i} variant="outline">{skill}</Badge>
              ))}
            </div>
          </div>
          
          {mentor.calendly_url && (
            <div>
              <h4 className="text-sm font-medium mb-1">Calendly</h4>
              <div className="flex items-center">
                <CalendarClock className="h-4 w-4 mr-2 text-muted-foreground" />
                <a 
                  href={mentor.calendly_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline"
                >
                  View Calendar
                </a>
              </div>
            </div>
          )}
          
          <div>
            <h4 className="text-sm font-medium mb-1">Status</h4>
            <StatusBadge status={mentor.status} />
          </div>
        </div>
        
        <DialogFooter className="sm:justify-between">
          <DialogClose asChild>
            <Button variant="outline">Close</Button>
          </DialogClose>
          
          {mentor.status === 'pending' && (
            <div className="flex gap-2">
              <Button
                variant="default"
                className="bg-green-500 hover:bg-green-600"
                onClick={() => {
                  onUpdateStatus(mentor.id, 'approved');
                  onOpenChange(false);
                }}
              >
                <Check className="h-4 w-4 mr-2" />
                Approve
              </Button>
              
              <Button
                variant="destructive"
                onClick={() => {
                  onUpdateStatus(mentor.id, 'rejected');
                  onOpenChange(false);
                }}
              >
                <X className="h-4 w-4 mr-2" />
                Reject
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MentorDetailsDialog;
